import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck, LogOut } from 'lucide-react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import {
  activityLogApi, notificationRulesApi, permissionsApi, rolesApi, systemSettingsApi,
  userRolesApi, userSessionsApi, usersApi,
} from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ALWAYS_INCLUDED_MODULES, PAGE_ACCESS_MAP } from '../../components/ILMSPageAccessPicker';
import { AppearanceTab } from './AppearanceTab';
import { EntryFormsTab } from './EntryFormsTab';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { ILMSFilterBar } from '../../components/ILMSFilterBar';
import { ILMSSelect } from '../../components/ILMSForm';
import { ILMSSpinner } from '../../components/ILMSLoadingState';
import { DEFAULT_PAGE_SIZE, ILMSPagination } from '../../components/ILMSPagination';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSModal } from '../../components/ILMSModal';
import { useReferenceData } from '../../features/common/referenceData';
import { useDebounce } from '../../hooks/useDebounce';
import { cn } from '../../utils/cn';
import { formatDateTime } from '../../utils/format';
import type {
  ActivityLog, NotificationRule, PermissionActionValue, Role, SystemSetting, User, UserRoleAssignment, UserSession,
} from '../../types';
import type { FieldConfig, ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: every Security-category table (Users, Roles &
// Permissions, User Roles, Sessions, Notification Rules, Settings, Audit
// Log) is Administration — one page, seven tabs — since they're all
// admin-only configuration surfaces rather than daily operator workflows.

const usersConfig: ModuleConfig<User> = {
  title: 'Users', entityLabel: 'user', module: 'Users', queryKey: 'users', idKey: 'id',
  api: {
    ...usersApi,
    create: async (v: any) => {
      const created = await usersApi.create({
        username: v.username, email: v.email || undefined, phone: v.phone || undefined,
        status: v.status, password: v.password,
      } as any);
      // Page access is a separate call (the user doesn't exist yet at
      // submit time) — an empty/untouched list here just means
      // unrestricted, so skip the call entirely rather than sending a
      // pointless empty PUT.
      if (Array.isArray(v.page_access) && v.page_access.length > 0) {
        const modules = [...new Set([...v.page_access, ...ALWAYS_INCLUDED_MODULES])];
        try { await usersApi.updateModuleAccess(created.id, modules); } catch { /* user itself is saved either way */ }
      }
      return created;
    },
    update: async (id: string, v: any) => {
      const updated = await usersApi.update(id, {
        email: v.email || undefined, phone: v.phone || undefined,
        status: v.status, must_change_password: v.must_change_password,
      } as any);
      if (Array.isArray(v.page_access)) {
        const modules = v.page_access.length > 0 ? [...new Set([...v.page_access, ...ALWAYS_INCLUDED_MODULES])] : [];
        try { await usersApi.updateModuleAccess(id, modules); } catch { /* user's own fields are already saved */ }
      }
      // Blank means "leave it as-is" — only reset when the admin actually
      // typed a new one. Deliberately NOT swallowed like page_access/photo
      // failures above: a silently-failed password reset could leave an
      // admin believing they'd changed it when they hadn't, which matters
      // more than losing the polish of a partial-success message.
      if (v.new_password) {
        await usersApi.resetPassword(id, v.new_password);
      }
      return updated;
    },
  },
  // page_access has no backing column on User itself — read back from
  // restricted_modules (see app/models/rbac.py's UserModuleAccess) so
  // reopening an existing user for edit shows their current restriction
  // instead of defaulting to "unrestricted" every time.
  deriveEditValues: (row) => ({ page_access: row.restricted_modules ?? [] }),
  columns: [
    { key: 'username', header: 'Username', primary: true, render: (r) => r.username },
    { key: 'email', header: 'Email', render: (r) => r.email || '—' },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
    { key: 'access', header: 'Page Access', render: (r) => {
      const visibleCount = (r.restricted_modules ?? []).filter((m) => !ALWAYS_INCLUDED_MODULES.includes(m) && !m.includes('::')).length;
      return visibleCount > 0 ? `Restricted (${visibleCount})` : 'Unrestricted';
    } },
  ],
  fields: [
    { name: 'username', label: 'Username', type: 'text', section: 'Account', required: true },
    { name: 'email', label: 'Email', type: 'text', section: 'Account', optional: true },
    { name: 'phone', label: 'Phone', type: 'text', section: 'Account', optional: true },
    { name: 'status', label: 'Status', type: 'select', section: 'Account', required: true, staticOptions: [
      { value: 'Active', label: 'Active' }, { value: 'Locked', label: 'Locked' }, { value: 'Disabled', label: 'Disabled' },
    ] },
    // The backend requires a password on create (no account can exist
    // without one) but has no password field on update at all — changing
    // an existing user's password is a separate flow, not this form. Create
    // was silently failing on every submission before this field existed:
    // the form had nothing to send, the API 422'd on the missing field, and
    // the only visible sign was a generic "Field required" banner with no
    // input to point at.
    { name: 'password', label: 'Password', type: 'password', section: 'Account',
      required: (v) => v.id === undefined, visibleWhen: (v) => v.id === undefined,
      helperText: 'At least 8 characters. The user can change this after signing in, and an Admin/Super Admin can always reset it later from here.' },
    // Admin-side reset (edit mode only) — separate from the create-only
    // password field above so it reads unambiguously as "leave blank to
    // keep the current one" rather than looking like a required re-entry.
    { name: 'new_password', label: 'Reset Password', type: 'password', section: 'Account',
      optional: true, visibleWhen: (v) => v.id !== undefined,
      helperText: 'Leave blank to keep their current password. Setting one here forces them to choose their own on next login.' },
    { name: 'page_access', label: 'Page Access', type: 'pageAccess', section: 'Page Access', optional: true, fullWidth: true,
      helperText: 'Optional — restricts this specific user to only the pages/tabs checked below, on top of whatever their assigned Role(s) already allow. Leave everything unchecked for full, unrestricted access based on their Role(s).' },
  ] as FieldConfig<any>[],
};

const userRolesConfig: ModuleConfig<UserRoleAssignment> = {
  title: 'User Roles', entityLabel: 'role assignment', module: 'Roles & Permissions', queryKey: 'user-roles', api: userRolesApi, idKey: 'id',
  referenceKeys: ['users', 'roles', 'farms'], addLabel: 'Assign Role',
  columns: [
    { key: 'user', header: 'User', primary: true, render: (r) => r.user_id },
    { key: 'role', header: 'Role', render: (r) => r.role_id },
    { key: 'from', header: 'Effective From', render: (r) => r.effective_from },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'user_id', label: 'User', type: 'select', section: 'Assignment', required: true, optionsKey: 'users' },
    { name: 'role_id', label: 'Role', type: 'select', section: 'Assignment', required: true, optionsKey: 'roles' },
    { name: 'farm_id', label: 'Farm (optional — blank applies everywhere)', type: 'select', section: 'Assignment', optional: true, optionsKey: 'farms' },
    { name: 'effective_from', label: 'Effective From', type: 'date', section: 'Validity', required: true },
    { name: 'effective_to', label: 'Effective To', type: 'date', section: 'Validity', optional: true },
    { name: 'status', label: 'Status', type: 'select', section: 'Validity', required: true, staticOptions: [
      { value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' },
    ] },
  ],
};

const notificationRulesConfig: ModuleConfig<NotificationRule> = {
  title: 'Notification Rules', entityLabel: 'rule', module: 'Notifications', queryKey: 'notification-rules', api: notificationRulesApi, idKey: 'id',
  referenceKeys: ['farms'], defaultValues: { active: true },
  columns: [
    { key: 'type', header: 'Rule Type', primary: true, render: (r) => r.rule_type },
    { key: 'threshold', header: 'Threshold', render: (r) => r.threshold_value ?? '—' },
    { key: 'roles', header: 'Notify Roles', render: (r) => r.notify_roles || '—' },
    { key: 'active', header: 'Active', render: (r) => <ILMSStatusBadge status={r.active ? 'Active' : 'Inactive'} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Rule', optional: true, optionsKey: 'farms' },
    { name: 'rule_type', label: 'Rule Type', type: 'select', section: 'Rule', required: true, staticOptions: [
      { value: 'VaccinationDue', label: 'Vaccination Due' }, { value: 'DewormingDue', label: 'Deworming Due' },
      { value: 'PregnancyCheckDue', label: 'Pregnancy Check Due' }, { value: 'CalvingDue', label: 'Calving Due' },
      { value: 'StockLow', label: 'Stock Low' }, { value: 'StockExpiry', label: 'Stock Expiry' },
      { value: 'MilkDrop', label: 'Milk Drop' }, { value: 'EggDrop', label: 'Egg Drop' },
    ] },
    { name: 'threshold_value', label: 'Threshold', type: 'decimal', section: 'Rule', optional: true },
    { name: 'notify_roles', label: 'Notify Roles (comma-separated)', type: 'text', section: 'Rule', optional: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Rule' },
  ],
};

const systemSettingsConfig: ModuleConfig<SystemSetting> = {
  title: 'Settings', entityLabel: 'setting', module: 'System Settings', queryKey: 'system-settings', api: systemSettingsApi, idKey: 'id',
  referenceKeys: ['farms'], defaultValues: { data_type: 'STRING' },
  columns: [
    { key: 'key', header: 'Key', primary: true, render: (r) => r.setting_key },
    { key: 'value', header: 'Value', render: (r) => r.setting_value },
    { key: 'type', header: 'Type', render: (r) => r.data_type, hideOnTablet: true },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm (optional — blank is global)', type: 'select', section: 'Setting', optional: true, optionsKey: 'farms' },
    { name: 'setting_key', label: 'Key', type: 'text', section: 'Setting', required: true },
    { name: 'setting_value', label: 'Value', type: 'text', section: 'Setting', required: true },
    { name: 'data_type', label: 'Data Type', type: 'select', section: 'Setting', required: true, staticOptions: [
      { value: 'STRING', label: 'Text' }, { value: 'INT', label: 'Whole Number' }, { value: 'DECIMAL', label: 'Decimal' },
      { value: 'BOOLEAN', label: 'Yes/No' }, { value: 'JSON', label: 'Structured (JSON)' },
    ] },
    { name: 'description', label: 'Description', type: 'textarea', section: 'Setting', optional: true, fullWidth: true },
  ],
};

const ACTIONS: PermissionActionValue[] = ['View', 'Create', 'Edit', 'Delete/Archive', 'Approve', 'Export'];

function RolesPermissionsTab() {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list({ page_size: 50 }) });
  const permissionsQuery = useQuery({ queryKey: ['permissions'], queryFn: () => permissionsApi.list() });

  const roles = rolesQuery.data?.items ?? [];
  const [selectedRoleId, setSelectedRoleId] = useState('');

  useEffect(() => {
    if (!selectedRoleId && roles.length > 0) setSelectedRoleId(roles[0].id);
  }, [roles, selectedRoleId]);

  const matrixQuery = useQuery({
    queryKey: ['role-permissions', selectedRoleId],
    queryFn: () => rolesApi.permissions(selectedRoleId),
    enabled: !!selectedRoleId,
  });

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setChecked(new Set((matrixQuery.data ?? []).map((p) => p.id)));
    setSaved(false);
  }, [matrixQuery.data]);

  const modules = useMemo(() => {
    const set = new Set((permissionsQuery.data ?? []).map((p) => p.module));
    return Array.from(set).sort();
  }, [permissionsQuery.data]);

  const permissionsByModuleAction = useMemo(() => {
    const map = new Map<string, string>();
    (permissionsQuery.data ?? []).forEach((p) => map.set(`${p.module}|${p.action}`, p.id));
    return map;
  }, [permissionsQuery.data]);

  const toggle = (permissionId: string | undefined) => {
    if (!permissionId) return;
    setSaved(false);
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: () => rolesApi.setPermissions(selectedRoleId, Array.from(checked)),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['role-permissions', selectedRoleId] });
    },
  });

  const selectedRole = roles.find((r: Role) => r.id === selectedRoleId);

  return (
    <div className="flex flex-col gap-4">
      {rolesQuery.isError && <ILMSAlert tone="critical">{getApiErrorMessage(rolesQuery.error, 'Unable to load roles.')}</ILMSAlert>}

      <div className="flex gap-2 flex-wrap">
        {roles.map((r: Role) => (
          <button
            key={r.id}
            onClick={() => setSelectedRoleId(r.id)}
            className={cn(
              'h-10 px-3.5 rounded-md text-sm font-medium border',
              selectedRoleId === r.id ? 'bg-brand-green-700 text-white border-brand-green-700' : 'bg-white text-ink-700 border-ink-300/30 hover:border-brand-green-400'
            )}
          >
            {r.name}
          </button>
        ))}
      </div>

      <ILMSCard
        title={selectedRole ? `${selectedRole.name} — Permission Matrix` : 'Permission Matrix'}
        subtitle={selectedRole?.purpose ?? undefined}
        actions={
          <ILMSButton icon={<Check />} onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!selectedRoleId}>
            Save Changes
          </ILMSButton>
        }
        padded={false}
      >
        {saved && <div className="px-5 pt-4"><ILMSAlert tone="success">Permissions updated for {selectedRole?.name}.</ILMSAlert></div>}
        {saveMutation.isError && <div className="px-5 pt-4"><ILMSAlert tone="critical">{getApiErrorMessage(saveMutation.error, 'Unable to save permission changes.')}</ILMSAlert></div>}
        {permissionsQuery.isLoading || matrixQuery.isLoading || !selectedRoleId ? (
          <div className="p-5"><ILMSSpinner label="Loading permission matrix…" /></div>
        ) : (
          <div className="overflow-x-auto p-4 sm:p-5">
            <table className="w-full border-collapse min-w-[720px]">
              <thead>
                <tr>
                  <th className="text-label text-ink-500 text-left py-2 px-2 sticky left-0 bg-white">Module</th>
                  {ACTIONS.map((a) => (
                    <th key={a} className="text-label text-ink-500 text-center py-2 px-2 whitespace-nowrap">{a}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => (
                  <tr key={module} className="border-t border-ink-300/10">
                    <td className="text-table-text text-ink-900 font-medium py-2 px-2 sticky left-0 bg-white whitespace-nowrap flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-ink-300" /> {module}
                    </td>
                    {ACTIONS.map((action) => {
                      const permissionId = permissionsByModuleAction.get(`${module}|${action}`);
                      const isChecked = permissionId ? checked.has(permissionId) : false;
                      return (
                        <td key={action} className="text-center py-2 px-2">
                          <input
                            type="checkbox"
                            disabled={!permissionId}
                            checked={isChecked}
                            onChange={() => toggle(permissionId)}
                            className="h-5 w-5 rounded border-ink-300/50 text-brand-green-700 disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-brand-green-500"
                            aria-label={`${module} - ${action}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ILMSCard>
    </div>
  );
}

function UserSessionsTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const listQuery = useQuery({
    queryKey: ['user-sessions', page, pageSize],
    queryFn: () => userSessionsApi.list({ page, page_size: pageSize }),
  });
  const revokeMutation = useMutation({
    mutationFn: (id: string) => userSessionsApi.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-sessions'] }),
  });

  const columns: ILMSTableColumn<UserSession>[] = [
    { key: 'user', header: 'User', primary: true, render: (r) => r.user_id },
    { key: 'login', header: 'Login', render: (r) => formatDateTime(r.login_at) },
    { key: 'logout', header: 'Logout', render: (r) => (r.logout_at ? formatDateTime(r.logout_at) : '—') },
    { key: 'ip', header: 'IP Address', render: (r) => r.ip_address || '—', hideOnTablet: true },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => r.status === 'Active' ? (
        <ILMSButton size="sm" variant="ghost" icon={<LogOut />} onClick={() => revokeMutation.mutate(r.id)} loading={revokeMutation.isPending && revokeMutation.variables === r.id}>
          Revoke
        </ILMSButton>
      ) : null,
    },
  ];

  return (
    <ILMSCard padded={false}>
      <div className="p-4 sm:p-5">
        <ILMSTable
          columns={columns}
          data={listQuery.data?.items ?? []}
          rowKey={(r) => r.id}
          isLoading={listQuery.isLoading}
          error={listQuery.isError ? getApiErrorMessage(listQuery.error) : null}
          onRetry={() => listQuery.refetch()}
          emptyTitle="No sessions recorded yet."
        />
        {!listQuery.isLoading && !listQuery.isError && (listQuery.data?.total ?? 0) > 0 && (
          <ILMSPagination page={page} pageSize={pageSize} total={listQuery.data?.total ?? 0} onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </div>
    </ILMSCard>
  );
}

const AUDIT_ACTIONS = [
  'LOGIN', 'LOGOUT', 'LOGIN_FAILED', 'CREATE', 'UPDATE', 'ARCHIVE', 'DELETE', 'APPROVE', 'REJECT',
  'EXPORT', 'IMPORT', 'SCAN_QR', 'ROLE_CHANGE', 'SETTINGS_CHANGE', 'BACKUP', 'RESTORE', 'POST_FINANCE', 'API_SYNC',
];

// The real permission-module strings, deduped straight from the same map
// Page Access already uses — one list to keep in sync, not a second copy.
const AUDIT_MODULES = Array.from(new Set(PAGE_ACCESS_MAP.flatMap((p) => p.tabs.map((t) => t.module)))).sort();

function ProfileField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-helper-text text-ink-400">{label}</dt>
      <dd className="text-table-text text-ink-900 font-medium truncate">{value}</dd>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  if (value == null) return null;
  return (
    <div>
      <p className="text-label text-ink-700 mb-1">{label}</p>
      <pre className="text-helper-text text-ink-700 bg-surface-subtle rounded-md p-2.5 overflow-x-auto whitespace-pre-wrap break-words">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function AuditLogDetailModal({ entry, onClose }: { entry: ActivityLog | null; onClose: () => void }) {
  return (
    <ILMSModal open={!!entry} onClose={onClose} title="Activity detail" size="md">
      {entry && (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            <ProfileField label="Time" value={formatDateTime(entry.event_time)} />
            <ProfileField label="Result" value={<ILMSStatusBadge status={entry.result} size="sm" />} />
            <ProfileField label="User" value={entry.username_snapshot || 'System'} />
            <ProfileField label="Role" value={entry.role_snapshot || '—'} />
            <ProfileField label="Action" value={entry.action.replace(/_/g, ' ')} />
            <ProfileField label="Module" value={entry.module} />
            <ProfileField label="Entity Type" value={entry.entity_type || '—'} />
            <ProfileField label="Record" value={entry.record_key || '—'} />
            <ProfileField label="IP Address" value={entry.ip_address || '—'} />
            <ProfileField label="Request ID" value={entry.request_id || '—'} />
          </dl>
          {entry.device_info && (
            <div>
              <p className="text-label text-ink-700 mb-1">Device / Browser</p>
              <p className="text-secondary text-ink-500 break-words">{entry.device_info}</p>
            </div>
          )}
          {entry.failure_reason && (
            <ILMSAlert tone="critical" title="Failure reason">
              {entry.failure_reason}
            </ILMSAlert>
          )}
          <JsonBlock label="Before" value={entry.old_values} />
          <JsonBlock label="After" value={entry.new_values} />
        </div>
      )}
    </ILMSModal>
  );
}

function AuditLogTab() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [action, setAction] = useState('');
  const [module, setModule] = useState('');
  const [result, setResult] = useState('');
  const [userId, setUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detailEntry, setDetailEntry] = useState<ActivityLog | null>(null);
  const { data: refData } = useReferenceData(['users']);

  const filters = { action, module, result, userId, dateFrom, dateTo };
  const activeCount = Object.values(filters).filter(Boolean).length + (debouncedSearch ? 1 : 0);
  const clearFilters = () => {
    setSearch(''); setAction(''); setModule(''); setResult(''); setUserId(''); setDateFrom(''); setDateTo('');
  };

  const listQuery = useQuery({
    queryKey: ['audit-log', page, pageSize, debouncedSearch, action, module, result, userId, dateFrom, dateTo],
    queryFn: () =>
      activityLogApi.list({
        page, page_size: pageSize,
        q: debouncedSearch || undefined,
        action: action || undefined,
        module: module || undefined,
        result: result || undefined,
        user_id: userId || undefined,
        date_from: dateFrom ? `${dateFrom}T00:00:00` : undefined,
        date_to: dateTo ? `${dateTo}T23:59:59` : undefined,
      }),
  });

  const columns: ILMSTableColumn<ActivityLog>[] = [
    { key: 'timestamp', header: 'Time', primary: true, render: (r) => formatDateTime(r.event_time) },
    { key: 'user', header: 'User', render: (r) => r.username_snapshot || 'System' },
    { key: 'role', header: 'Role', render: (r) => r.role_snapshot || '—', hideOnTablet: true },
    { key: 'action', header: 'Action', render: (r) => r.action.replace(/_/g, ' ') },
    { key: 'module', header: 'Module', render: (r) => r.module },
    { key: 'record', header: 'Record', render: (r) => r.record_key || '—', hideOnTablet: true },
    { key: 'result', header: 'Result', render: (r) => <ILMSStatusBadge status={r.result} size="sm" /> },
  ];

  return (
    <ILMSCard padded={false}>
      <div className="p-4 sm:p-5 border-b border-ink-300/10">
        <ILMSFilterBar
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Search by user, record, or failure reason…"
          activeCount={activeCount}
          onClear={() => { clearFilters(); setPage(1); }}
        >
          <ILMSSelect value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="w-full lg:w-44">
            <option value="">All actions</option>
            {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </ILMSSelect>
          <ILMSSelect value={module} onChange={(e) => { setModule(e.target.value); setPage(1); }} className="w-full lg:w-44">
            <option value="">All modules</option>
            {AUDIT_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
          </ILMSSelect>
          <ILMSSelect value={result} onChange={(e) => { setResult(e.target.value); setPage(1); }} className="w-full lg:w-36">
            <option value="">Any result</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
          </ILMSSelect>
          <ILMSSelect value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }} className="w-full lg:w-44">
            <option value="">All users</option>
            {refData.users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
          </ILMSSelect>
          <ILMSDatePicker label="From" value={dateFrom} max={dateTo || undefined} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} containerClassName="w-full lg:w-40" />
          <ILMSDatePicker label="To" value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} containerClassName="w-full lg:w-40" />
        </ILMSFilterBar>
      </div>
      <div className="p-4 sm:p-5">
        <ILMSTable
          columns={columns}
          data={listQuery.data?.items ?? []}
          rowKey={(r) => r.id}
          onRowClick={(r) => setDetailEntry(r)}
          isLoading={listQuery.isLoading}
          error={listQuery.isError ? getApiErrorMessage(listQuery.error) : null}
          onRetry={() => listQuery.refetch()}
          emptyTitle="No activity recorded yet."
          emptyDescription={activeCount > 0 ? 'Try different filters.' : 'Actions such as sign-ins, record changes and approvals will be listed here as they happen.'}
        />
        {!listQuery.isLoading && !listQuery.isError && (listQuery.data?.total ?? 0) > 0 && (
          <ILMSPagination page={page} pageSize={pageSize} total={listQuery.data?.total ?? 0} onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </div>
      <AuditLogDetailModal entry={detailEntry} onClose={() => setDetailEntry(null)} />
    </ILMSCard>
  );
}

export default function AdminPage() {
  return (
    <TabbedPage
      title="Administration"
      subtitle="Users, roles and permissions, sessions, alert rules, settings and the audit trail."
      tabs={[
        { id: 'users', label: 'Users', render: () => <ResourceModule config={usersConfig} /> },
        { id: 'roles-permissions', label: 'Roles & Permissions', render: () => <RolesPermissionsTab /> },
        { id: 'user-roles', label: 'User Roles', render: () => <ResourceModule config={userRolesConfig} /> },
        { id: 'sessions', label: 'Sessions', render: () => <UserSessionsTab /> },
        { id: 'notification-rules', label: 'Notification Rules', render: () => <ResourceModule config={notificationRulesConfig} /> },
        { id: 'settings', label: 'Settings', render: () => <ResourceModule config={systemSettingsConfig} /> },
        { id: 'appearance', label: 'Appearance', render: () => <AppearanceTab /> },
        { id: 'entry-forms', label: 'Entry Form Settings', render: () => <EntryFormsTab /> },
        { id: 'audit-log', label: 'Audit Log', render: () => <AuditLogTab /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Administration', usersConfig, userRolesConfig, notificationRulesConfig, systemSettingsConfig);
